import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.startsWith('/auth/')) {
      localStorage.removeItem('user');
      localStorage.removeItem('session_expires_at');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// Package APIs
export const packageAPI = {
  getAll: (params) => api.get('/packages', { params }),
  getPublicSummary: (destination) => api.get('/packages/public-summary', { params: destination ? { destination } : {} }),
  getPublicSelling: (params = {}) => api.get('/packages/public-selling', { params }),
  // Admin endpoint that returns ALL packages including unavailable
  getAllAdmin: () => api.get('/packages/admin'),
  getFeatured: () => api.get('/packages/featured'),
  getById: (id) => api.get(`/packages/${id}`),
  getBySlug: (slug) => api.get(`/packages/by-slug/${slug}`),
  create: (data) => api.post('/packages', data),
  update: (id, data) => api.put(`/packages/${id}`, data),
  delete: (id) => api.delete(`/packages/${id}`),
  copy: (id) => api.post(`/packages/${id}/copy`),
  migrateSlugs: () => api.post('/packages/migrate-slugs'),
  getDestinations: () => api.get('/destinations'),
  downloadBrochure: (id, params = {}) => api.get(`/packages/${id}/brochure`, { 
    params,
    responseType: 'blob' 
  }),
  // Bulk import/export
  exportExcel: () => api.get('/packages/export/excel', { responseType: 'blob' }),
  downloadTemplate: () => api.get('/packages/export/template', { responseType: 'blob' }),
  bulkImport: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/packages/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Booking APIs
export const bookingAPI = {
  getAll: () => api.get('/bookings'),
  getById: (id) => api.get(`/bookings/${id}`),
  create: (data) => api.post('/bookings', data),
  updateStatus: (id, status) => api.put(`/bookings/${id}/status`, { status }),
  generateInvoice: (id) => api.post(`/bookings/${id}/invoice`),
};

// Invoice APIs
export const invoiceAPI = {
  getAll: () => api.get('/invoices'),
  getById: (id) => api.get(`/invoices/${id}`),
};

// Report APIs
export const reportAPI = {
  getMonthly: (year, month) => api.get('/reports/monthly', { params: { year, month } }),
  exportExcel: (year, month) => api.get('/reports/export/excel', { params: { year, month }, responseType: 'blob' }),
  exportCSV: (year, month) => api.get('/reports/export/csv', { params: { year, month }, responseType: 'blob' }),
};

// Admin APIs
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  updateUserStatus: (id, status) => api.put(`/admin/users/${id}/status`, { status }),
  resetUserPassword: (id, newPassword) => api.put(`/admin/users/${id}/reset-password`, { new_password: newPassword }),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Confirmed Bookings APIs
export const confirmedBookingAPI = {
  getAll: () => api.get('/confirmed-bookings'),
  create: (data) => api.post('/confirmed-bookings', data),
  update: (id, data) => api.put(`/confirmed-bookings/${id}`, data),
  delete: (id) => api.delete(`/confirmed-bookings/${id}`),
  exportCSV: () => api.get('/reports/export/confirmed-csv', { responseType: 'blob' }),
  getSalesSummary: () => api.get('/reports/sales-summary'),
};

// Location APIs
export const locationAPI = {
  getAll: () => api.get('/locations'),
  add: (name) => api.post('/locations', { name }),
  delete: (name) => api.delete(`/locations/${name}`),
};

// Duration APIs
export const durationAPI = {
  getAll: () => api.get('/durations'),
  add: (nights) => api.post('/durations', { nights }),
  delete: (nights) => api.delete(`/durations/${nights}`),
};

// Media Gallery APIs
export const mediaAPI = {
  getAll: () => api.get('/media'),
  upload: (file, altText = '', tags = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('alt_text', altText);
    formData.append('tags', tags);
    return api.post('/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  delete: (id) => api.delete(`/media/${id}`),
  getFileUrl: (filename) => `${BACKEND_URL}/api/media/files/${filename}`,
};

// Email APIs
export const emailAPI = {
  sendPackageQuote: (data) => api.post('/email/package-quote', data),
  sendBookingConfirmation: (data) => api.post('/email/booking-confirmation', data),
  sendInvoice: (data) => api.post('/email/invoice', data),
};

// Health Check
export const healthAPI = {
  check: () => api.get('/health'),
};

// SEO APIs
export const seoAPI = {
  getPackageSEO: (packageId) => api.get(`/seo/package/${packageId}`),
  getSettings: () => api.get('/seo/settings'),
  saveSettings: (data) => api.post('/seo/settings', data),
  getPageSettings: (pagePath) => api.get(`/seo/settings/${pagePath}`),
  // Redirect management
  getRedirects: () => api.get('/seo/redirects'),
  createRedirect: (data) => api.post('/seo/redirects', data),
  updateRedirect: (id, data) => api.put(`/seo/redirects/${id}`, data),
  deleteRedirect: (id) => api.delete(`/seo/redirects/${id}`),
  toggleRedirect: (id) => api.patch(`/seo/redirects/${id}/toggle`),
};

// Analytics APIs
export const analyticsAPI = {
  getDashboard: () => api.get('/analytics/dashboard'),
  getDestinations: () => api.get('/analytics/destinations'),
  getAgents: () => api.get('/analytics/agents'),
  getTrends: (period = 'monthly', months = 12) => api.get(`/analytics/trends?period=${period}&months=${months}`),
  getComparison: () => api.get('/analytics/compare'),
  
  // User Activity Tracking
  track: (eventType, eventData, pageUrl, sessionId) => 
    api.post('/analytics/track', { event_type: eventType, event_data: eventData, page_url: pageUrl, session_id: sessionId }),
  getUserActivity: (days = 30) => api.get(`/analytics/user-activity?days=${days}`),
  getSearchPatterns: (days = 30) => api.get(`/analytics/search-patterns?days=${days}`),
  getHotelViews: (days = 30) => api.get(`/analytics/hotel-views?days=${days}`),
  getDownloads: (days = 30) => api.get(`/analytics/downloads?days=${days}`),
  getConversions: (days = 30) => api.get(`/analytics/conversions?days=${days}`),
  getActivityFeed: (limit = 50, eventType = null) => 
    api.get('/analytics/activity-feed', { params: { limit, event_type: eventType } }),
  getUserJourney: (sessionId) => api.get(`/analytics/user-journey/${sessionId}`),
  exportActivity: (days = 30, eventType = null) => 
    api.get('/analytics/export-activity', { params: { days, event_type: eventType } }),
  
  // NEW: Comprehensive User Activity Tracking
  trackLogin: () => api.post('/analytics/track-login'),
  trackPackageView: (packageId, packageName, destination, timeSpent = 0) => 
    api.post('/analytics/track-package-view', null, { params: { package_id: packageId, package_name: packageName, destination, time_spent_seconds: timeSpent } }),
  trackInteraction: (interactionType, packageId = null, packageName = null, extraData = null) =>
    api.post('/analytics/track-interaction', null, { params: { interaction_type: interactionType, package_id: packageId, package_name: packageName }, data: extraData }),
  
  // User Activity Dashboard
  getUserLogins: (params = {}) => api.get('/analytics/user-logins', { params }),
  getPackageViews: (params = {}) => api.get('/analytics/package-views', { params }),
  getUserInteractions: (params = {}) => api.get('/analytics/user-interactions', { params }),
  getActiveUsers: (inactiveDays = 30) => api.get(`/analytics/active-users?inactive_days=${inactiveDays}`),
  getComprehensiveDashboard: (params = {}) => api.get('/analytics/comprehensive-dashboard', { params }),
  exportUserActivity: (params = {}) => api.get('/analytics/export-user-activity', { params }),
};

// Site Settings APIs
export const siteSettingsAPI = {
  get: () => api.get('/settings/site'),
  getAdmin: () => api.get('/settings/site/admin'),
  save: (data) => api.post('/settings/site', data),
  getTopBar: () => api.get('/settings/top-bar'),
  getFooter: () => api.get('/settings/footer'),
  getDepartureCities: () => api.get('/settings/departure-cities'),
  getDestinationsWithDepartures: () => api.get('/settings/destinations-with-departures'),
};

// Hotel Booking APIs
export const hotelAPI = {
  // Cities
  getCities: (includeInactive = false) => api.get('/hotels/cities', { params: { include_inactive: includeInactive } }),
  getCity: (cityId) => api.get(`/hotels/cities/${cityId}`),
  createCity: (data) => api.post('/hotels/cities', data),
  updateCity: (cityId, data) => api.put(`/hotels/cities/${cityId}`, data),
  deleteCity: (cityId) => api.delete(`/hotels/cities/${cityId}`),
  
  // Hotels
  getHotels: (params = {}) => api.get('/hotels/list', { params }),
  getHotel: (hotelId) => api.get(`/hotels/detail/${hotelId}`),
  createHotel: (data) => api.post('/hotels/create', data),
  updateHotel: (hotelId, data) => api.put(`/hotels/update/${hotelId}`, data),
  deleteHotel: (hotelId) => api.delete(`/hotels/delete/${hotelId}`),
  createCompleteHotel: (data) => api.post('/hotels/create-complete', data),
  
  // Hotel Images/Media
  getHotelImages: (hotelId) => api.get(`/hotels/${hotelId}/images`),
  addHotelImage: (hotelId, data) => api.post(`/hotels/${hotelId}/images`, data),
  deleteHotelImage: (hotelId, imageId) => api.delete(`/hotels/${hotelId}/images/${imageId}`),
  setPrimaryImage: (hotelId, imageId) => api.put(`/hotels/${hotelId}/images/${imageId}/primary`),
  reorderHotelImages: (hotelId, imageIds) => api.put(`/hotels/${hotelId}/images/reorder`, imageIds),
  
  // Hotel Priority Management
  getFeaturedHotels: (cityId) => api.get('/hotels/featured', { params: { city_id: cityId } }),
  getCityFeaturedHotels: (cityId) => api.get(`/hotels/featured/${cityId}`),
  updateHotelPriority: (hotelId, data) => api.put(`/hotels/priority/${hotelId}`, null, { params: data }),
  bulkUpdatePriorities: (data) => api.post('/hotels/priority/bulk', data),
  removeHotelPriority: (hotelId, cityId) => api.delete(`/hotels/priority/${hotelId}`, { params: { city_id: cityId } }),
  
  // Room Categories
  getRoomCategories: (params = {}) => api.get('/hotels/rooms', { params }),
  getRoomCategory: (roomId) => api.get(`/hotels/rooms/${roomId}`),
  createRoomCategory: (data) => api.post('/hotels/rooms', data),
  updateRoomCategory: (roomId, data) => api.put(`/hotels/rooms/${roomId}`, data),
  deleteRoomCategory: (roomId) => api.delete(`/hotels/rooms/${roomId}`),
  
  // Hotel-Room Connections (Pricing)
  getConnections: (hotelId, cityId) => api.get('/hotels/connections', { params: { hotel_id: hotelId, city_id: cityId } }),
  getConnection: (connectionId) => api.get(`/hotels/connections/${connectionId}`),
  createConnection: (data) => api.post('/hotels/connections', data),
  updateConnection: (connectionId, data) => api.put(`/hotels/connections/${connectionId}`, data),
  deleteConnection: (connectionId) => api.delete(`/hotels/connections/${connectionId}`),
  
  // Search
  search: (data) => api.post('/hotels/search', data),
  
  // Bookings
  getBookings: (params = {}) => api.get('/hotels/bookings', { params }),
  getMyBookings: (params = {}) => api.get('/hotels/my-bookings', { params }),
  getBooking: (bookingId) => api.get(`/hotels/bookings/${bookingId}`),
  createBooking: (data) => api.post('/hotels/bookings', data),
  updateBookingStatus: (bookingId, status) => api.put(`/hotels/bookings/${bookingId}/status`, null, { params: { status } }),
  
  // Bulk Upload & Export
  bulkUpload: (data) => api.post('/hotels/bulk-upload', data),
  exportHotels: (params = {}) => api.get('/hotels/export', { params }),
  
  // Homepage Settings
  getHomepageSettings: () => api.get('/hotels/homepage/settings'),
  saveHomepageSettings: (data) => api.post('/hotels/homepage/settings', data),
  getHomepageFeaturedHotels: () => api.get('/hotels/homepage/featured-hotels'),
  
  // Hotel Booking Reports
  getBookingReportsSummary: () => api.get('/hotels/reports/summary'),
  getBookingReports: (params = {}) => api.get('/hotels/reports/bookings', { params }),
  getAgentsForReports: () => api.get('/hotels/reports/agents'),
  
  // Rate Calendar & Bulk Pricing
  getRateCalendar: (params = {}) => api.get('/hotels/rate-calendar', { params }),
  previewBulkPricing: (data) => api.post('/hotels/bulk-pricing-preview', data),
  applyBulkPricing: (data) => api.post('/hotels/bulk-pricing-update', data),
  getBulkPricingLogs: (params = {}) => api.get('/hotels/bulk-pricing-logs', { params }),
  updateSingleRate: (connectionId, rateId, data) => api.put('/hotels/rate-calendar/update-rate', null, { 
    params: { connection_id: connectionId, rate_id: rateId, ...data } 
  }),
  
  // Bulk Rate Upload (New Format)
  bulkRateUpload: (data) => api.post('/hotels/bulk-rate-upload', data),
  getBulkRateUploadSample: () => api.get('/hotels/bulk-rate-upload/sample'),
  
  // Supreme Master Format Bulk Upload
  supremeMasterBulkUpload: (data) => api.post('/hotels/bulk-rate-upload/supreme-master', data),
  getSupremeMasterTemplate: () => api.get('/hotels/bulk-rate-upload/supreme-master/template'),
  
  // Special Charges (Gala Dinner)
  getSpecialCharges: (params = {}) => api.get('/hotels/special-charges', { params }),
  getHotelSpecialCharges: (hotelId) => api.get(`/hotels/special-charges/hotel/${hotelId}`),
  getSpecialCharge: (chargeId) => api.get(`/hotels/special-charges/${chargeId}`),
  createSpecialCharge: (data) => api.post('/hotels/special-charges', data),
  updateSpecialCharge: (chargeId, data) => api.put(`/hotels/special-charges/${chargeId}`, data),
  deleteSpecialCharge: (chargeId) => api.delete(`/hotels/special-charges/${chargeId}`),
  
  // Seed data
  seedData: () => api.post('/hotels/seed'),
  
  // Hotel Master - Central Rate Database
  getHotelMaster: (params = {}) => api.get('/hotels/hotel-master', { params }),
  getHotelMasterStats: () => api.get('/hotels/hotel-master/stats'),
  updateHotelMasterRate: (rateId, data) => api.put(`/hotels/hotel-master/${rateId}`, data),
  createHotelMasterRate: (data) => api.post('/hotels/hotel-master', data),
  duplicateHotelMasterRate: (rateId) => api.post(`/hotels/hotel-master/${rateId}/duplicate`),
  deleteHotelMasterRate: (rateId) => api.delete(`/hotels/hotel-master/${rateId}`),
  exportHotelMaster: (params = {}) => api.get('/hotels/hotel-master/export', { params }),
  
  // Bulk operations
  bulkDeleteHotelMasterRates: (rateIds) => api.post('/hotels/hotel-master/bulk-delete', { rate_ids: rateIds }),
  bulkUpdateHotelMasterRates: (rateIds, updateFields) => api.post('/hotels/hotel-master/bulk-update', { rate_ids: rateIds, update_fields: updateFields }),
  deduplicateHotelMasterRates: () => api.post('/hotels/hotel-master/deduplicate'),
};

export default api;
